import { useState } from 'react';
import { CardTapPage, withCustomRender } from 'airkey-mfa-react';
import { useParams } from 'react-router';
import { rejectTx, validateTap } from '@/api/bff';
import { WEB_NFC_PERMISSIONS_VIDEO } from '@/components/3ds/TapCardPage/model/constants';
import { FullScreenLoader } from '@/components/LoadScreen/FullScreenLoader';
import { useTransaction } from '@/hooks/useTransaction/useTransaction';
import { AuthIntro } from '@/pages/AuthIntro/AuthIntro';
import { InstructionScreen } from '@/pages/Instructions/InstructionScreen';
import { NetworkError } from '@/pages/NetworkError/NetworkError';
import { Success } from '@/pages/Success/Success';
import { NoActiveRequestsError } from '@/pages/TransactionError/NoActiveRequestsError';
import { DeniedVerification } from '@/pages/Verification/DeniedVerification';
import { DenyVerificationModal } from '@/pages/Verification/DenyVerificationModal';
import { FailedVerification } from '@/pages/Verification/FailedVerification';
import { IncorrectCard } from '@/pages/Verification/IncorrectCard';
import { RetryVerification } from '@/pages/Verification/RetryVerification';
import { useAirKeyTrace, useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { ErrorType, getErrorType } from '@/utils/errors';
import { isMockFlowEnabled, MOCK_TAP_MESSAGE } from '@/utils/mockTap';

export function AntiVishing() {
  const { publicData, nonce } = useTransaction();
  const { intentOwner = '', workflow = '', policyProcessId = '' } = useParams();
  const [success, setSuccess] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [retry, setRetry] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [invalidPUID, setInvalidPUID] = useState(false);
  const [errorType, setErrorType] = useState<ErrorType | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeoutTracked, setTimeoutTracked] = useState(false);
  // Local mock flow (MOCK_BFF): once the user confirms, show the tap screen with
  // a dev "Simulate tap" control instead of driving the SDK's gated NFC reader.
  const [mockReading, setMockReading] = useState(false);
  const mockFlow = isMockFlowEnabled();

  useAirKeyTracePageView('card_tap');

  const sendTrace = useAirKeyTrace();

  async function handleValidateTap(message: string) {
    try {
      let puid = '';
      // Check for v1 card
      if (message.length === 60) {
        puid = message.slice(4, 20);
      }
      // Check for v2 card
      if (message.length === 80) {
        puid = message.substring(16, 32);
      }

      if (!publicData.puidArray?.includes(puid)) {
        setIsProcessing(false);
        setSuccess(false);
        setInvalidPUID(true);
        sendTrace({
          view: 'card_tap',
          action: 'wrong_card',
        });
        return;
      }

      const { validationResult } = await validateTap(
        policyProcessId,
        message,
        intentOwner,
        workflow,
      );

      setIsProcessing(false);

      if (validationResult === 'Success') {
        setSuccess(true);
        return;
      }

      setSuccess(false);
      if (validationResult === 'Retry') {
        setRetry(true);
      }

      if (validationResult === 'Failure') {
        setVerificationFailed(true);
      }
    } catch (error) {
      sendTrace({
        view: 'card_tap',
        action: 'bad_read',
      });
      setIsProcessing(false);
      setErrorType(getErrorType(error));
    }
  }

  async function handleRejectTx() {
    sendTrace({
      view: 'card_tap',
      action: 'deny',
    });

    try {
      const deletedSuccessfully = await rejectTx(
        policyProcessId,
        intentOwner,
        workflow,
      );

      if (deletedSuccessfully) {
        setCancelled(true);
      } else {
        setVerificationFailed(true);
      }
    } catch (error) {
      sendTrace({
        view: 'card_tap',
        action: 'system_error',
        additionalData: {
          during_action: 'DENY',
          error_type: 'api_failure',
          error_code: getErrorType(error),
          recovery_action: 'show_error_screen',
        },
      });
      setIsProcessing(false);
      setErrorType(getErrorType(error));
    }
  }

  if (isProcessing) {
    return <FullScreenLoader />;
  }

  if (errorType) {
    switch (errorType) {
      case 'noActiveRequests':
        return <NoActiveRequestsError />;
      default:
        return <NetworkError />;
    }
  }

  if (cancelled) {
    return <DeniedVerification />;
  }

  if (verificationFailed) {
    return <FailedVerification />;
  }

  if (success) {
    return <Success />;
  }

  const TapExperience = withCustomRender(
    CardTapPage,
    (wrappedComponent: any) => {
      const { state } = wrappedComponent;

      if (state.airKeyPayload) {
        return wrappedComponent.renderPayloadReceived();
      }

      // Track timeout events when the NFC reader times out waiting for a card tap
      if (state.timedOut && !timeoutTracked) {
        setTimeoutTracked(true);
        sendTrace({
          view: 'card_tap',
          action: 'timed_out',
        });
      }

      // TODO: Standardize how we use callbacks
      if (retry) {
        return (
          <RetryVerification
            onAccept={() => {
              setRetry(false);
              setTimeoutTracked(false);
            }}
          />
        );
      }

      if (!state.doReadCard && !mockReading && !invalidPUID) {
        return (
          <>
            <AuthIntro
              onAccept={() => {
                if (mockFlow) {
                  setMockReading(true);
                } else {
                  wrappedComponent.startReadAirKeyCard();
                }
              }}
              onUnrecognized={() => {
                setShowDenyModal(true);
              }}
            />
            {showDenyModal && (
              <DenyVerificationModal
                onDeny={() => {
                  wrappedComponent.cancelNdefAndClose();
                }}
                onGoBack={() => {
                  setShowDenyModal(false);
                }}
              />
            )}
          </>
        );
      }

      return (
        <>
          <InstructionScreen />
          {mockReading && (
            <button
              type="button"
              data-testid="mock-simulate-tap"
              onClick={() => onReadCardTap(MOCK_TAP_MESSAGE)}
              style={{
                position: 'fixed',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9999,
                padding: '12px 20px',
                border: '2px dashed #013d5b',
                borderRadius: 8,
                background: '#fff',
                color: '#013d5b',
                font: '600 14px sans-serif',
              }}
            >
              DEV: Simulate card tap
            </button>
          )}
          {invalidPUID && (
            <IncorrectCard
              onAccept={() => {
                setInvalidPUID(false);
              }}
            />
          )}
        </>
      );
    },
  );

  const onReadCardTap = async (cardReadData: string) => {
    setIsProcessing(true);
    sendTrace({
      view: 'card_tap',
      action: 'card_tapped',
    });
    await handleValidateTap(cardReadData);
  };

  return (
    <TapExperience
      nonce={nonce}
      onCardTapResult={onReadCardTap}
      close={handleRejectTx}
      webNfcPermissionsVideo={WEB_NFC_PERMISSIONS_VIDEO}
    />
  );
}
